import React, { useEffect, useRef, useState } from "react";
import Sidebar from "../../components/ui/Sidebar";
import Header from "../../components/ui/Header";
import SearchInput from "./components/SearchInput";
import Pagination from "./components/Pagination";
import Toast from "./components/Toast";
import DataTable from "./components/DataTable";
import HomeInspirationModal from "./components/InspirationModal";
import { Plus, File, Loader } from "lucide-react";
import DeleteModal from "./components/DeleteModal";

const apiUrl = import.meta.env.VITE_API_URL;

const HomeInspiration = () => {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [data, setData] = useState([]);
    const [toast, setToast] = useState({ show: false, message: "", type: "" });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [searchTriggered, setSearchTriggered] = useState(false);
    const searchClicked = useRef(false);
    const debounced = useRef(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const params = new URLSearchParams({
                search: searchQuery,
                page: currentPage,
                limit: itemsPerPage,
            });

            const res = await fetch(`${apiUrl}/api/admin-user/home-inspiration?${params}`);
            const json = await res.json();

            if (!res.ok) throw new Error(json.error || "Failed to fetch");

            setData(json.data || []);
            setTotalPages(json.totalPages || 0);
        } catch (err) {
            setToast({ show: true, message: "Failed to fetch data", type: "error" });
        } finally {
            setIsLoading(false);
        }
    };

    const toggleDisplay = async (item) => {
        try {
            const newDisplay = !item.isDisplay;
            const formData = new FormData();
            formData.append("id", item._id);
            formData.append("isDisplay", newDisplay);

            const res = await fetch(`${apiUrl}/api/admin-user/home-inspiration`, {
                method: "PUT",
                body: formData,
            });

            const json = await res.json();

            if (res.ok) {
                setData((prevData) =>
                    prevData.map((d) =>
                        d._id === item._id ? { ...d, isDisplay: !item.isDisplay } : d
                    )
                );
                setToast({ show: true, message: "Display status updated", type: "success" });
            } else {
                throw new Error(json.error || "Failed to update");
            }
        } catch (err) {
            console.error("Toggle failed:", err);
            setToast({ show: true, message: "Failed to toggle", type: "error" });
        }
    };

    const deleteInspiration = async () => {
        try {
            const res = await fetch(`${apiUrl}/api/admin-user/home-inspiration?id=${editItem._id}`, {
                method: "Delete",
            });

            const json = await res.json();

            if (res.ok) {
                setData((prevData) =>
                    prevData.filter((d) =>
                        d._id !== editItem._id
                    )
                );
                setToast({ show: true, message: "Inspiration deleted", type: "success" });
                setShowDeleteModal(false);
            } else {
                throw new Error(json.error || "Failed to update");
            }
        } catch (err) {
            setShowDeleteModal(false);
            console.error("delete failed:", err);
            setToast({ show: true, message: "Failed to deleted", type: "error" });
        }
    };

    useEffect(() => {
        if (!searchTriggered) {
            fetchData();
        }
    }, [currentPage, itemsPerPage]);

    useEffect(() => {
        if (searchTriggered) {
            fetchData();
            setSearchTriggered(false);
        }
    }, [searchTriggered]);

    useEffect(() => {
        if (searchClicked.current) {
            debounced.current = setTimeout(() => {
                setCurrentPage(1);
                setItemsPerPage(10);
                setSearchTriggered(true);
            }, 1000);
        }

        return () => clearTimeout(debounced.current);
    }, [searchQuery]);

    const columns = [
        { key: "sno", label: "Sno" },
        { key: "prompt", label: "Prompt" },
        { key: "image", label: "Image" },
        { key: "isDisplay", label: "Display", sortable: false },
        { key: "action", label: "Action" },
    ];

    console.log(showDeleteModal, 'fwygfywgfygytgweftygfwg');


    return (
        <div className="flex bg-background">
            <Sidebar variant="with-categories" onToggle={(collapsed) => setIsCollapsed(collapsed)} />
            <div className={`w-full transition-all duration-300 ${isCollapsed ? "lg:pl-16" : "lg:pl-64"}`}>
                <Header isCollapsed={isCollapsed} />

                <main className="overflow-y-auto p-4 md:p-6 bg-background">
                    <div className="flex justify-between items-center mb-6">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Home Inspiration</h1>
                            <p className="text-sm text-gray-500">Manage prompts and images</p>
                        </div>
                        <button
                            onClick={() => {
                                setEditItem(null);
                                setShowModal(true);
                            }}
                            className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 text-sm"
                        >
                            <Plus className="w-4 h-4" /> Create Home Inspiration
                        </button>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                        <div className="p-4 border-b flex items-center justify-between">
                            <SearchInput
                                placeholder="Search prompt..."
                                onClick={() => {
                                    searchClicked.current = true
                                }}
                                onBlur={() => {
                                    searchClicked.current = false;
                                }}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onClear={() => { setSearchQuery(""); searchClicked.current = true }}
                            />
                        </div>

                        <DataTable
                            columns={columns}
                            data={data}
                            currentPage={currentPage}
                            itemsPerPage={itemsPerPage}
                            isLoading={isLoading}
                            onEdit={(item) => {
                                setEditItem(item);
                                setShowModal(true);
                            }}
                            setEditItem={setEditItem}
                            setShowDeleteModal={setShowDeleteModal}
                            toggleDisplay={toggleDisplay}
                        />

                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                            itemsPerPage={itemsPerPage}
                            onItemsPerPageChange={setItemsPerPage}
                            totalItems={data.length}
                        />
                    </div>
                </main>

                {showModal && (
                    <HomeInspirationModal
                        onClose={() => {
                            setShowModal(false);
                            setEditItem(null);
                        }}
                        onSuccess={() => {
                            setToast({ show: true, message: "Saved successfully", type: "success" });
                            setShowModal(false);
                            fetchData();
                        }}
                        editItem={editItem}
                    />
                )}

                {toast.show && (
                    <Toast
                        message={toast.message}
                        type={toast.type}
                        onClose={() => setToast({ ...toast, show: false })}
                    />
                )}
                {showDeleteModal &&
                    <DeleteModal
                        onClose={() => setShowDeleteModal(false)}
                        onConfirm={deleteInspiration}
                    />
                }
            </div>
        </div>
    );
};

export default HomeInspiration;